// FixedBottomNav - Avatar-only at rest, menu rises on hover
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, Shield, User, Eye, BookOpen, KeyRound, LogOut } from 'lucide-react';
import axios from 'axios';
import { logoutAndRedirect } from '@/utils/axiosConfig';
import { normalizeAvatarUrl } from '@/utils/avatar';
import { toast } from 'sonner';
import { ClipperState } from '@/components/clipper/ClipperState';
import { BrandLockup } from '@/components/brand/Brand';

// Self-fetched profile cache (module-level so route changes don't refetch).
// Pages that pass a full currentUser (My Desk, Project Hub) never trigger
// the fetch; the global instance (Admin Settings, Pipeline, Diagnostics…)
// resolves the authenticated user's profile itself — same source
// UserDashboard uses (GET /api/users/{id}/profile).
//
// 2026-07-16 — Cache invalidation for successful avatar uploads.  Prior
// to this, the module-level cache was populated at first mount and
// NEVER refreshed, so uploading a new portrait photo returned 200 but
// the lower-left kept rendering the stale (placeholder) `avatar` value.
// Any surface that mutates the current user's profile MUST dispatch
// `window.dispatchEvent(new CustomEvent('keymade:user-updated', {detail: {...profile}}))`
// so the cache flips and the avatar re-renders on the next paint.
let _profileCache = null;
let _profilePromise = null;

export function resetProfileCache(nextProfile = null) {
  _profileCache = nextProfile;
  _profilePromise = null;
}

const fetchOwnProfile = () => {
  if (_profileCache) return Promise.resolve(_profileCache);
  if (!_profilePromise) {
    const userId = localStorage.getItem('userId');
    const token = localStorage.getItem('accessToken');
    if (!userId || !token) return Promise.resolve(null);
    _profilePromise = axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/profile`)
      .then((res) => {
        const u = res.data || {};
        _profileCache = {
          name: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || 'User',
          avatar: u.avatar || null,
          role: u.role,
        };
        return _profileCache;
      })
      .catch(() => {
        _profilePromise = null; // allow retry on a later mount
        return null;
      });
  }
  return _profilePromise;
};

export function FixedBottomNav({ 
  darkMode = false, 
  currentUser,
  availableRoles = [],
  onOpenProfile,
  onSwitchRole,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  // 2026-06-21 (FIX 5 item 2) — HOVER LIVES ON THE AVATAR, NOT THE COLUMN.
  // The open/close state used to be a single `hovered` boolean set by
  // onMouseEnter/onMouseLeave on the OUTER wrapper. The wrapper contains the
  // menu, which stays mounted (opacity-0) and keeps its flex layout space, so
  // the wrapper's hit box was 449 px tall against a 40 px avatar — hovering
  // anywhere in that column opened the menu.
  //
  // Three phases now, so a leave can FADE instead of vanish:
  //   'closed'  invisible, pointer-events-none
  //   'open'    visible, pointer-events-auto
  //   'fading'  fading to 0 over 700 ms and STILL pointer-events-auto, so the
  //             cursor can come back and cancel the fade with no click. If this
  //             became pointer-events-none while fading, re-entry would be
  //             impossible — that is the easy way to get this wrong.
  const [phase, setPhase] = useState('closed');
  const [menuOpen, setMenuOpen] = useState(false);
  const fadeTimer = useRef(null);
  const [fetchedUser, setFetchedUser] = useState(_profileCache);

  const cancelFade = useCallback(() => {
    if (fadeTimer.current) {
      clearTimeout(fadeTimer.current);
      fadeTimer.current = null;
    }
  }, []);

  const openMenu = useCallback(() => {
    cancelFade();
    setPhase('open');
  }, [cancelFade]);

  const startFade = useCallback(() => {
    if (menuOpen) return; // pinned by click — hover never closes it
    cancelFade();
    setPhase('fading');
    fadeTimer.current = setTimeout(() => {
      fadeTimer.current = null;
      setPhase('closed');
    }, 700);
  }, [menuOpen, cancelFade]);

  useEffect(() => cancelFade, [cancelFade]);

  // Choosing an item dismisses the menu immediately — pinned or not.
  const closeNow = useCallback(() => {
    cancelFade();
    setMenuOpen(false);
    setPhase('closed');
  }, [cancelFade]);

  // Component-level fallback: when no usable currentUser is passed (the
  // global mount), resolve the profile once and reuse it everywhere.
  const hasUsableUser = Boolean(currentUser?.avatar || currentUser?.profile_photo);
  // DP61 Part 5 — this used to bail out when the caller passed an avatar, which
  // left the authoritative NAME unfetched on exactly the page (My Desk) that
  // passed the hardcoded one. It now always resolves the profile once; the
  // module-level cache means one request per session either way.
  useEffect(() => {
    if (fetchedUser) return;
    let alive = true;
    fetchOwnProfile().then((p) => { if (alive && p) setFetchedUser(p); });
    return () => { alive = false; };
  }, [fetchedUser]);

  // 2026-07-16 — Listen for cross-component profile mutations (avatar
  // uploads, name edits, role switches). Any handler that mutates the
  // owner's own profile MUST dispatch this event so the lower-left
  // avatar reflects the change without a page reload.
  useEffect(() => {
    const onUserUpdated = (ev) => {
      const detail = ev?.detail || {};
      resetProfileCache(null);
      // Fast-path: patch the in-component copy so the next paint uses
      // the new avatar without waiting for the refetch.
      setFetchedUser((prev) => ({
        ...(prev || {}),
        ...(detail.name ? { name: detail.name } : {}),
        ...(detail.avatar !== undefined ? { avatar: detail.avatar } : {}),
        ...(detail.role ? { role: detail.role } : {}),
      }));
      // Then re-fetch to catch anything the caller didn't include.
      fetchOwnProfile().then((p) => { if (p) setFetchedUser(p); });
    };
    window.addEventListener('keymade:user-updated', onUserUpdated);
    return () => window.removeEventListener('keymade:user-updated', onUserUpdated);
  }, []);

  // DP61 Part 5 — ONE identity. The NAME now comes from the fetched profile
  // FIRST (GET /api/users/{id}/profile, first_name + last_name) and only falls
  // back to whatever the caller passed. It used to be the other way round, so a
  // caller's hint always won: App.js passed localStorage 'userName' (a
  // login-time cache) and My Desk passed a HARDCODED 'Chad Holgerson'. Same user,
  // two initials — TU on /settings/admin, CH on the shell pages.
  // The AVATAR keeps caller-first, deliberately: a page that has just uploaded
  // one holds a fresher value than the cached profile.
  const effectiveUser = hasUsableUser
    // DP109 ruling 6c — THE FETCHED AVATAR WINS. This took the avatar from the
    // caller's prop only, so on My Desk — the one page that passes its own
    // `userProfile?.avatar` — a photo changed in the dialog did not reach the
    // rail until a reload, even though the `keymade:user-updated` handler above
    // had already patched `fetchedUser`. The name was fixed this way in DP61
    // Part 5; the avatar was left behind.
    ? { ...currentUser, name: fetchedUser?.name || currentUser?.name,
        avatar: fetchedUser?.avatar !== undefined ? fetchedUser.avatar : currentUser?.avatar }
    : {
        ...(fetchedUser || {}),
        ...(currentUser || {}),
        name: fetchedUser?.name || currentUser?.name,
        avatar: currentUser?.avatar || fetchedUser?.avatar,
        role: currentUser?.role || fetchedUser?.role,
      };

  const isOnCornerPage = location.pathname === '/corner';
  const isOnVaultPage = location.pathname === '/vault';

  const roleInfo = availableRoles.find(r => r.value === effectiveUser?.role);

  /* DP106 Part 3b — WHY THE INITIALS WERE NEVER DRAWN. This took `name` only,
     and the shell's user object carries `first_name` and `last_name`; when the
     fetched profile had no `name`, it returned the literal 'U' at best and the
     span was empty at worst. The Admin screen computes from first/last, which
     is why initials appear there and not here. It now reads whatever the record
     actually holds, in order, and falls back to the email. */
  const getUserInitials = (user) => {
    const name = typeof user === 'string' ? user : (user?.name
      || [user?.first_name, user?.last_name].filter(Boolean).join(' ')
      || user?.email || '');
    if (!name) return 'U';
    const parts = name.split(' ');
    return parts.length >= 2 
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.substring(0, 2).toUpperCase();
  };

  const rawAvatarUrl = normalizeAvatarUrl(effectiveUser?.avatar || effectiveUser?.profile_photo);
  /* DP106 Part 3c — WHAT TRIGGERS THE FALLBACK: the img's own error event, held
     in state. It used to be an imperative poke — `e.target.style.display =
     'none'; e.target.nextSibling.style.display = 'flex'` — which fought the
     `hidden` class React had already put on the span and left a 40x40 box the
     same colour as the rail behind it. React decides now: a url that fails is
     a url we do not have. */
  const [imgFailed, setImgFailed] = React.useState(false);
  React.useEffect(() => { setImgFailed(false); }, [rawAvatarUrl]);
  const avatarUrl = imgFailed ? null : rawAvatarUrl;

  const expanded = phase === 'open' || menuOpen;

  return (
    <div
      // FIX 5 item 1 — bottom-10 (40 px) instead of bottom-3 (12 px): the
      // avatar's lower edge used to overlap the "KEY-MADE v1.6.2" line by
      // 20 px. `pointer-events-none` on the wrapper so this column stops being
      // a 449 px invisible hit target; the avatar and the menu opt back in.
      /* DP106 Part 1a — WHY THE ACCOUNT MARK COULD NOT BE REACHED AT ALL.
         This wrapper sat at Tailwind `z-[100]`. The rail — `.km-rail`, the
         192 px column this mark is drawn inside — carries `--km-z-rail: 3000`
         and `position: relative`, so it painted OVER the avatar on every
         shell page. `document.elementFromPoint` at the avatar's centre
         returned `ASIDE.km-rail`, not the button: a real mouse could never
         land on it, and Playwright only ever "opened" the menu because
         `force=True` skips hit-testing. The token layer already names this
         surface: `--km-z-menu` is 6000, and the token's own note reads
         "menus and popovers: Admin, avatar, dropdowns". So it now sits where
         the scale always said it does, not at a hand-written number that
         happened to be below the rail. */
      className="fixed bottom-10 left-3 hidden md:flex flex-col items-start pointer-events-none"
      style={{ zIndex: 'var(--km-z-menu)' }}
      data-testid="fixed-bottom-nav"
    >
      {/* Rising menu — opens only from the avatar, closes with a slow fade */}
      <div
        onMouseEnter={openMenu}
        onMouseLeave={startFade}
        data-testid="fixed-bottom-nav-menu"
        data-menu-phase={menuOpen ? 'pinned' : phase}
        className={`flex flex-col mb-1.5 origin-bottom transition-all ease-out ${
          expanded
            ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto duration-[120ms]'
            : phase === 'fading'
              ? 'opacity-0 translate-y-0 scale-100 pointer-events-auto duration-700'
              : 'opacity-0 translate-y-3 scale-95 pointer-events-none duration-300'
        }`}
      >
        <div
          data-km-surface={darkMode ? 'dark' : undefined}
          className={`rounded-xl overflow-hidden shadow-xl border ${
          darkMode 
            ? 'bg-[#141414]/95 border-[#2A2A2A] backdrop-blur-md' 
            : 'bg-white/95 border-gray-200/80 backdrop-blur-md shadow-lg'
        }`} style={{ width: 180 }}>
          {/* Quick Nav Links */}
          <div className="p-1.5 space-y-0.5">
            {/* DP59 — "Users" removed. It went to /team, and Admin Tools' "Team"
                goes to the same place. One path, two labels, two menus. This menu
                keeps personal things; administration lives in the Admin square. */}
            <button
              onClick={() => navigate('/corner')}
              className={`group w-full flex items-center gap-2.5 rounded-lg px-3 py-2 transition-all duration-150 ${
                isOnCornerPage
                  ? darkMode ? 'bg-purple-500/15 text-purple-300' : 'bg-purple-50 text-purple-700'
                  : darkMode ? 'text-gray-300 hover:bg-white/5 hover:text-white' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
              data-testid="corner-of-desk-fixed"
            >
              <Lock className="w-4 h-4 shrink-0" />
              <span className="text-sm font-medium">Corner of the Desk</span>
            </button>

            <button
              onClick={() => navigate('/vault')}
              className={`group w-full flex items-center gap-2.5 rounded-lg px-3 py-2 transition-all duration-150 ${
                isOnVaultPage
                  ? darkMode ? 'bg-[#70170F]/20 text-[#E5967A]' : 'bg-[#FFF5F2] text-[#70170F]'
                  : darkMode ? 'text-gray-300 hover:bg-white/5 hover:text-white' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
              data-testid="vault-fixed-nav"
            >
              <Shield className="w-4 h-4 shrink-0" />
              <span className="text-sm font-medium">Vault</span>
            </button>
          </div>

          {/* Divider */}
          <div className={`mx-2 border-t ${darkMode ? 'border-white/5' : 'border-gray-100'}`} />

          {/* Profile & Role Actions */}
          <div className="p-1.5 space-y-0.5">
            <button
              /* DP106 Part 1d — THE SILENT FALLTHROUGH IS GONE. This used to
                 fall through to navigate('/settings/account'), which is the
                 Change Password screen below, so on every page but My Desk the
                 two buttons did the same thing. The account menu is shell
                 furniture, so the dialog is mounted in AppShell and reached by
                 an event; a page that passes its own `onOpenProfile` (My Desk)
                 still wins. If neither can open it, the button SAYS SO. */
              onClick={() => {
                closeNow();
                if (onOpenProfile) { onOpenProfile(); return; }
                if (typeof window !== 'undefined'
                    && window.dispatchEvent(new CustomEvent('km:open-profile', { cancelable: true }))
                    === false) return;
                toast.error('Your profile could not be opened. Nothing else was opened in its place.');
              }}
              className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2 transition-all duration-150 ${
                darkMode ? 'text-gray-300 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50'
              }`}
              data-testid="profile-btn-fixed"
            >
              <User className="w-4 h-4 shrink-0" />
              <span className="text-sm font-medium">My Profile</span>
            </button>
            <button
              onClick={() => { navigate('/settings/account'); closeNow(); }}
              className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2 transition-all duration-150 ${
                darkMode ? 'text-gray-300 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50'
              }`}
              data-testid="change-password-nav-btn"
            >
              <KeyRound className="w-4 h-4 shrink-0" />
              <span className="text-sm font-medium">Change Password</span>
            </button>
            {/* DP29 — the clipper is installed by every user on every machine
                they sit at, so it lives here, in the "me and this machine"
                menu, as a STATE rather than a link. Not Admin Settings: that
                is where an owner configures the firm, once. */}
            <div className={`px-3 py-2 ${darkMode ? 'border-t border-white/5' : 'border-t border-gray-100'}`}
                 data-testid="clipper-account-menu-row">
              <ClipperState compact darkMode={darkMode} testId="clipper-account-menu" />
            </div>
            <button
              onClick={() => navigate('/blueprints')}
              className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2 transition-all duration-150 ${
                darkMode ? 'text-gray-300 hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <BookOpen className="w-4 h-4 shrink-0" />
              <span className="text-sm font-medium">Help & Guides</span>
            </button>
            {onSwitchRole && (
              <button
                onClick={() => { onSwitchRole(); closeNow(); }}
                className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2 transition-all duration-150 ${
                  darkMode ? 'text-amber-400/80 hover:bg-white/5' : 'text-amber-600 hover:bg-amber-50'
                }`}
              >
                <Eye className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium">Switch Role</span>
              </button>
            )}
          </div>

          {/* Divider */}
          <div className={`mx-2 border-t ${darkMode ? 'border-white/5' : 'border-gray-100'}`} />

          {/* Log out — real logout flow: server-side refresh-token
              invalidation (best-effort) + session clear + /login */}
          <div className="p-1.5">
            <button
              onClick={() => { closeNow(); logoutAndRedirect(); }}
              className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2 transition-all duration-150 ${
                darkMode ? 'text-red-400/90 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-50'
              }`}
              data-testid="logout-btn-fixed"
            >
              <LogOut className="w-4 h-4 shrink-0" />
              <span className="text-sm font-medium">Log out</span>
            </button>
          </div>

          {/* Branding — DP32 §4: ONE lockup component. Two-colour wordmark,
              "Engineered by" as a credit line at 8.5px/0.16em. */}
          <div className={`px-3 py-1.5 border-t ${darkMode ? 'border-white/5' : 'border-gray-100'}`}>
            <BrandLockup onDark={darkMode} testId="engineered-by" />
          </div>
        </div>
      </div>

      {/* Avatar Circle - always visible. The ONLY hover trigger. */}
      <button
        onClick={() => {
          cancelFade();
          setMenuOpen((prev) => {
            const next = !prev;
            setPhase(next ? 'open' : 'closed');
            return next;
          });
        }}
        onMouseEnter={openMenu}
        onMouseLeave={startFade}
        onFocus={openMenu}
        onBlur={startFade}
        // DP60 Part 0d — the avatar wears the SAME box as the rail: .km-railbox
        // is 40x40, 1px border, 6px radius. It was 36x36 with no border and a
        // 2px radius, which made it the one exception at the foot. It keeps the
        // active ring, because that is state and not size.
        className={`km-railbox pointer-events-auto km-account-mark relative overflow-hidden cursor-pointer ${
          expanded ? 'km-railbox--active ring-2 ring-[#BC5D41] ring-offset-2' : ''
        } ${darkMode ? 'ring-offset-[#0D0D0D]' : 'ring-offset-white'}`}
        data-testid="user-menu-fixed"
      >
        {avatarUrl ? (
          // 2026-02 avatar-render fix — MUST use a relative `/api/...`
          // src for same-origin cookies to attach.  Previously we
          // prepended `${REACT_APP_BACKEND_URL}` which baked in the
          // preview host at build time; on production (or any deploy
          // whose page origin ≠ REACT_APP_BACKEND_URL) the <img> was
          // cross-origin, `SameSite=Lax` blocked the `keymade_access`
          // cookie, the auth-gated /api/uploads/* returned 401, onError
          // fired, and initials were rendered even for users with a real
          // photo.  Emitting the stored `/api/uploads/…` path verbatim
          // makes the browser resolve against the current page origin,
          // so the HttpOnly staff cookie ALWAYS attaches.  Absolute URLs
          // (e.g. legacy Dropbox share links) fall through unchanged.
          <img 
            src={avatarUrl}
            alt={effectiveUser?.name || 'User'}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="fixed-bottom-nav-avatar-img"
            onError={() => setImgFailed(true)}
          />
        ) : null}
        {/* DP106 Part 3a — the initials are the mark's GROUND, not an
            alternative to the picture: they are always drawn and the photo
            covers them. Rendering them only on the error event left the mark
            EMPTY for as long as a slow avatar request stayed in flight, which
            the testing agent caught at over 13 seconds on My Desk. */}
        <span className="km-railbox__letters km-account-mark__initials"
              data-testid="fixed-bottom-nav-avatar-initials">
          {getUserInitials(effectiveUser)}
        </span>
      </button>
    </div>
  );
}

export default FixedBottomNav;
